/**
 * Effective Permissions Preview Hook (Sprint 5 S5-A)
 *
 * Wraps POST /api/v1/admin/staff/{userId}/effective-permissions/preview to
 * compute the BEFORE / AFTER permission set the FI Admin will produce by
 * granting a hypothetical role-id set, alongside the precomputed diff
 * (added/removed permissions, added/removed ABAC predicates). Used by the
 * "Edit Roles" dialog to show consequences before the FI Admin commits the
 * `useUpdateStaffRoles` mutation -- the canonical preview endpoint shipped
 * in Sprint 4.
 *
 * The endpoint requires `staff:read`. It is read-only with respect to the
 * staff member's stored role set; the FI Admin still must call
 * `useUpdateStaffRoles` to persist (which carries its own step-up
 * requirement when fi_admin is in the proposed set).
 *
 * Sprint 1 deviation honored: the response uses the canonical
 * `EffectivePermissionsSet` shape (permissions + abacPredicates), not a
 * roleSlug-array shape -- ABAC predicates ride alongside permission
 * strings since FI Staff RBAC v2.
 */

import { useMutation } from '@tanstack/react-query';
import { fiStaffLifecycleClient } from '../api/client';
import type { components } from '@aaimplatform/institutional-types/fi-staff-lifecycle';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type PreviewEffectivePermissionsRequest = components['schemas']['PreviewEffectivePermissionsRequest'];
export type PreviewEffectivePermissionsResponse = components['schemas']['PreviewEffectivePermissionsResponse'];
export type EffectivePermissionsSet = components['schemas']['EffectivePermissionsSet'];
export type EffectivePermissionsDiff = components['schemas']['EffectivePermissionsDiff'];
export type AbacPredicateRef = components['schemas']['AbacPredicateRef'];

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

function unwrap<T>(result: FetchResult<T>): T {
  if (result.error || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string; error?: string } | undefined;
    const message = errorBody?.message ?? errorBody?.error ?? `HTTP ${status}: ${statusText}`;
    throw new Error(message);
  }
  return result.data;
}

async function previewEffectivePermissions(
  userId: string,
  body: PreviewEffectivePermissionsRequest,
): Promise<PreviewEffectivePermissionsResponse> {
  const result = await fiStaffLifecycleClient.POST(
    '/api/v1/admin/staff/{userId}/effective-permissions/preview',
    {
      params: { path: { userId } },
      body,
    },
  );
  return unwrap(result);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Compute the effective-permission preview for a staff member if the
 * supplied hypothetical role set were granted. Returns a `current`,
 * `proposed`, and `diff` payload. Read-only: does NOT modify the staff
 * member's stored roles. Pair with `useUpdateStaffRoles` to commit.
 *
 * Variables: `{ userId: string; body: { hypotheticalRoleIds: string[] } }`
 */
export function useEffectivePermissionsPreview() {
  return useMutation<
    PreviewEffectivePermissionsResponse,
    Error,
    { userId: string; body: PreviewEffectivePermissionsRequest }
  >({
    mutationFn: ({ userId, body }) => previewEffectivePermissions(userId, body),
  });
}
